// src/utils/sportFocusAreas.js
//
// Sport-specific focus areas organised by level progression.
// Used in video upload, session capture, and coaching workflows.
// Each area has a key, label, level range, and sport.

export const SPORT_FOCUS_AREAS = {
  ski: {
    label: 'Skiing',
    levels: [
      {
        level: 'beginner',
        label: 'Beginner',
        areas: [
          { key: 'snowplough', label: 'Snowplough / Pizza' },
          { key: 'balance_basic', label: 'Basic Balance' },
          { key: 'speed_control_basic', label: 'Speed Control' },
          { key: 'stopping', label: 'Stopping' },
          { key: 'confidence', label: 'Confidence' },
          { key: 'stance', label: 'Stance & Posture' },
        ],
      },
      {
        level: 'intermediate',
        label: 'Intermediate',
        areas: [
          { key: 'parallel_turns', label: 'Parallel Turns' },
          { key: 'edge_control', label: 'Edge Control' },
          { key: 'balance', label: 'Balance' },
          { key: 'outside_ski', label: 'Outside Ski Pressure' },
          { key: 'pole_plant', label: 'Pole Plant' },
          { key: 'turn_shape', label: 'Turn Shape' },
          { key: 'upper_body', label: 'Upper Body Separation' },
          { key: 'hip_position', label: 'Hip Position' },
          { key: 'weight_transfer', label: 'Weight Transfer' },
          { key: 'rhythm', label: 'Rhythm & Timing' },
        ],
      },
      {
        level: 'advanced',
        label: 'Advanced',
        areas: [
          { key: 'short_turns', label: 'Short Turns' },
          { key: 'carving', label: 'Carving' },
          { key: 'moguls', label: 'Moguls' },
          { key: 'steeps', label: 'Steep Terrain' },
          { key: 'off_piste', label: 'Off-Piste / Powder' },
          { key: 'absorption', label: 'Terrain Absorption' },
          { key: 'speed_high', label: 'High Speed Control' },
          { key: 'variable_terrain', label: 'Variable Terrain' },
        ],
      },
      {
        level: 'expert',
        label: 'Expert',
        areas: [
          { key: 'race_technique', label: 'Race Technique' },
          { key: 'dynamic_carving', label: 'Dynamic Carving' },
          { key: 'park_jumps', label: 'Park & Jumps' },
          { key: 'backcountry', label: 'Backcountry' },
          { key: 'competition', label: 'Competition Prep' },
        ],
      },
    ],
  },
  snowboard: {
    label: 'Snowboarding',
    levels: [
      {
        level: 'beginner',
        label: 'Beginner',
        areas: [
          { key: 'stance_sb', label: 'Stance & Balance' },
          { key: 'falling_leaf', label: 'Falling Leaf' },
          { key: 'heel_edge', label: 'Heel Edge Control' },
          { key: 'toe_edge', label: 'Toe Edge Control' },
          { key: 'stopping_sb', label: 'Stopping' },
          { key: 'confidence_sb', label: 'Confidence' },
        ],
      },
      {
        level: 'intermediate',
        label: 'Intermediate',
        areas: [
          { key: 'linking_turns', label: 'Linking Turns' },
          { key: 'toe_heel_transitions', label: 'Toe/Heel Transitions' },
          { key: 'edge_engagement', label: 'Edge Engagement' },
          { key: 'upper_body_sb', label: 'Upper Body' },
          { key: 'speed_control_sb', label: 'Speed Control' },
          { key: 'turn_shape_sb', label: 'Turn Shape' },
          { key: 'balance_sb', label: 'Balance' },
          { key: 'posture_sb', label: 'Posture' },
        ],
      },
      {
        level: 'advanced',
        label: 'Advanced',
        areas: [
          { key: 'carving_sb', label: 'Carving' },
          { key: 'switch_riding', label: 'Switch Riding' },
          { key: 'steeps_sb', label: 'Steep Terrain' },
          { key: 'powder_sb', label: 'Powder' },
          { key: 'moguls_sb', label: 'Moguls' },
          { key: 'ollie_nollie', label: 'Ollie & Nollie' },
          { key: 'variable_sb', label: 'Variable Terrain' },
        ],
      },
      {
        level: 'expert',
        label: 'Expert',
        areas: [
          { key: 'park_sb', label: 'Park & Freestyle' },
          { key: 'halfpipe', label: 'Halfpipe' },
          { key: 'backcountry_sb', label: 'Backcountry' },
          { key: 'spins', label: '180s / 360s' },
          { key: 'grabs', label: 'Grabs' },
          { key: 'rails', label: 'Rails & Boxes' },
        ],
      },
    ],
  },
};

/**
 * Get all focus areas for a sport, flattened with level info.
 * @param {string} sport — 'ski' or 'snowboard'
 * @returns {Array<{key, label, level, levelLabel}>}
 */
export function getAllFocusAreas(sport) {
  const sportConfig = SPORT_FOCUS_AREAS[sport];
  if (!sportConfig) return [];

  const result = [];
  for (const levelGroup of sportConfig.levels) {
    for (const area of levelGroup.areas) {
      result.push({
        ...area,
        level: levelGroup.level,
        levelLabel: levelGroup.label,
      });
    }
  }
  return result;
}

/**
 * Get focus areas relevant for a given level and below.
 * A student at 'intermediate' sees beginner + intermediate areas.
 * @param {string} sport
 * @param {string} currentLevel — 'beginner' | 'intermediate' | 'advanced' | 'expert'
 * @returns {Array<{key, label, level, levelLabel}>}
 */
export function getFocusAreasForLevel(sport, currentLevel) {
  const sportConfig = SPORT_FOCUS_AREAS[sport];
  if (!sportConfig) return [];

  const levelOrder = ['beginner', 'intermediate', 'advanced', 'expert'];
  const maxIdx = levelOrder.indexOf(currentLevel || 'intermediate');
  const cutoff = maxIdx >= 0 ? maxIdx + 1 : 2; // default to including intermediate

  const result = [];
  for (let i = 0; i < Math.min(cutoff + 1, sportConfig.levels.length); i++) {
    const levelGroup = sportConfig.levels[i];
    for (const area of levelGroup.areas) {
      result.push({
        ...area,
        level: levelGroup.level,
        levelLabel: levelGroup.label,
      });
    }
  }
  return result;
}
